<script setup lang="ts">
import { useAuthStore } from "@/stores/authStore.ts";
import { useQueryClient } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { useForm } from "vee-validate";
import { useI18n } from "vue-i18n";
import { object } from "yup";
import { type TCredentials } from "@/api/models/auth/jwt";
import { useRouter } from "vue-router";
import { computed, onMounted, ref } from "vue";
import { useLoginHintBadgesBeforeLogin } from "@/composables/models/user/useLoginHintBadges";
import { EEnvironmentType, useEnvironmentStore } from "@/stores/environmentStore";
import { createRequiredField } from "@/utils/validationHelpers";
import { useFormSubmitFlowLock } from "@/composables/shared/useFormSubmitFlowLock";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";
import { useCompleteLogin } from "@/composables/models/auth/useCompleteLogin";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";
import LoginFooterLinks from "@/components/models/auth/LoginFooterLinks.vue";

definePage({
  name: "login",
  meta: {
    title: "models.auth.actions.logIn",
    layout: "login",
    requiresAuth: false,
  },
});

const authStore = useAuthStore();
const { currentEnv } = useEnvironmentStore();
const router = useRouter();

const { t } = useI18n();
const completeLogin = useCompleteLogin();
const queryClient = useQueryClient();

const redirectPath = router.currentRoute.value.query.redirect as string | undefined;

const cacheCleared = ref(false);

const { data: loginHintBadges } = useLoginHintBadgesBeforeLogin(
  computed(() => cacheCleared.value && currentEnv !== EEnvironmentType.PRODUCTION)
);

const validationSchema = object({
  email: createRequiredField(t, "generic.fields.yourEmail"),
  password: createRequiredField(t, "generic.fields.password"),
});

const { handleSubmit, setValues } = useForm({
  validationSchema,
});

const { mutateAsync: performLogin, isPending } = useGenericMutation<void, TCredentials>({
  mutationFn: async (credentials) => {
    return await authStore.login(credentials);
  },
  options: {
    onSuccess: () => completeLogin(redirectPath),
    onError: (error): void => {
      logError(error, {
        context: {
          feature: "login",
          step: "onError",
        },
      });
      // snackStore.showError(t("models.auth.login.error"));
    },
  },
});

const { runSubmitFlow, isSubmitFlowPending } = useFormSubmitFlowLock();

const submit = handleSubmit((values) => {
  void runSubmitFlow(async () => {
    await performLogin(values as TCredentials);
  });
});

onMounted(async () => {
  await queryClient.resetQueries();
  cacheCleared.value = true;
});
</script>

<template>
  <div class="tw:ml-auto! tw:w-full tw:lg:w-1/2 tw:h-full tw:flex tw:items-center tw:lg:relative">
    <v-form
      class="tw:w-full tw:lg:w-2/3 tw:lg:pb-8"
      :validation-schema="validationSchema"
      @submit.prevent="submit"
    >
      <div class="tw:hidden tw:lg:block tw:font-bold tw:text-2xl tw:md:text-4xl tw:!mb-1">
        {{ t("models.auth.actions.logIn") }}
      </div>
      <div class="tw:mt-8!">
        <text-field
          name="email"
          :label="t('generic.fields.yourEmail')"
          data-testid="email-login-input"
        />
        <text-field
          name="password"
          type="password"
          :label="t('generic.fields.password')"
          data-testid="password-login-input"
        />
      </div>
      <div class="tw:flex tw:justify-end tw:-mt-3! tw:mb-5!">
        <router-link
          :to="{ path: '/auth/reset-password', query: { redirect: redirectPath } }"
          class="tw:text-xs tw:font-medium text-secondary tw:no-underline tw:hover:underline"
        >
          {{ t("models.auth.actions.forgotPassword") }}
        </router-link>
      </div>

      <PrimaryButton
        class="tw:w-full"
        type="submit"
        :loading="isSubmitFlowPending || isPending"
        data-testid="login-submit-button"
      >
        {{ t("models.auth.actions.logIn") }}
      </PrimaryButton>

      <LoginFooterLinks />

      <div
        v-if="loginHintBadges?.length"
        class="tw:fixed tw:bottom-[calc(0.5rem_+_env(safe-area-inset-bottom))] tw:right-2 tw:flex tw:gap-2 tw:flex-col"
      >
        <div class="tw:gap-1 tw:flex">
          <v-chip
            v-for="badge in loginHintBadges"
            :key="badge.id"
            v-tippy="t('models.auth.login.loginAs', { email: badge.email })"
            :color="badge.color"
            variant="tonal"
            @click="
              setValues({
                email: badge.email,
                password: badge.password,
              });
              submit();
            "
          >
            {{ badge.name }}
          </v-chip>
        </div>
      </div>
    </v-form>
  </div>
</template>
